import { useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import {
  selectAllQuotes,
  selectDailyQuote,
  selectDailyQuoteId,
  selectLastSelectedDate,
} from '../store/selectors';
import { setDailyQuote } from '../store/quotesSlice';
import { getTodayDateString } from './dateHelpers';
import { selectDailyQuote as selectQuoteByDate, selectRandomQuote, shouldSelectNewQuote } from './dailyQuoteSelector';
import { Quote } from '../types/quote';

/**
 * Return type for useDailyQuote hook
 */
interface UseDailyQuoteReturn {
  quote: Quote | null; // Current daily quote
  isLoading: boolean; // True during initial selection
  refresh: () => void; // Manually select a different random quote
}

/**
 * Custom hook for managing the daily quote
 *
 * Automatically selects a new quote when:
 * - The date changes (new day)
 * - No quote is currently selected
 * - The selected quote was deleted
 *
 * Usage:
 * ```tsx
 * function HomePage() {
 *   const { quote, isLoading, refresh } = useDailyQuote();
 *
 *   if (isLoading) return <div>Loading...</div>;
 *   if (!quote) return <div>Add your first quote!</div>;
 *
 *   return (
 *     <div>
 *       <p>{quote.text}</p>
 *       <button onClick={refresh}>Show another quote</button>
 *     </div>
 *   );
 * }
 * ```
 *
 * @returns Object containing quote, loading state, and refresh function
 */
export function useDailyQuote(): UseDailyQuoteReturn {
  const dispatch = useAppDispatch();

  // Get state from Redux
  const allQuotes = useAppSelector(selectAllQuotes);
  const currentQuote = useAppSelector(selectDailyQuote);
  const dailyQuoteId = useAppSelector(selectDailyQuoteId);
  const lastSelectedDate = useAppSelector(selectLastSelectedDate);

  // Check if we need to select a new quote
  useEffect(() => {
    const today = getTodayDateString();

    // Determine if we should select a new quote
    const needsNewQuote = shouldSelectNewQuote(
      today,
      lastSelectedDate,
      dailyQuoteId,
      allQuotes
    );

    if (needsNewQuote) {
      // Select new quote based on today's date
      const newQuote = selectQuoteByDate(allQuotes, today);

      if (newQuote) {
        dispatch(
          setDailyQuote({
            quoteId: newQuote.id,
            date: today,
          })
        );
      }
    }
  }, [allQuotes, dailyQuoteId, lastSelectedDate, dispatch]);

  /**
   * Manually refresh to show a different random quote
   * This overrides the daily selection until the next day
   */
  const refresh = () => {
    // Select a random quote, excluding the current one
    const newQuote = selectRandomQuote(allQuotes, dailyQuoteId || undefined);

    if (newQuote) {
      const today = getTodayDateString();
      dispatch(
        setDailyQuote({
          quoteId: newQuote.id,
          date: today,
        })
      );
    }
  };

  return {
    quote: currentQuote,
    isLoading: allQuotes.length > 0 && !currentQuote,
    refresh,
  };
}

/**
 * Hook for manually forcing a refresh of the daily quote
 * Useful for "Refresh" buttons
 *
 * @returns Function to clear and reselect daily quote
 */
export function useRefreshDailyQuote(): () => void {
  const dispatch = useAppDispatch();
  const allQuotes = useAppSelector(selectAllQuotes);

  return () => {
    const today = getTodayDateString();
    const newQuote = selectQuoteByDate(allQuotes, today);

    if (newQuote) {
      dispatch(
        setDailyQuote({
          quoteId: newQuote.id,
          date: today,
        })
      );
    }
  };
}

/**
 * Hook for checking if the daily quote needs updating
 * Useful for background sync or periodic checks
 *
 * @returns Object with check function and current status
 */
export function useDailyQuoteCheck() {
  const dispatch = useAppDispatch();
  const allQuotes = useAppSelector(selectAllQuotes);
  const dailyQuoteId = useAppSelector(selectDailyQuoteId);
  const lastSelectedDate = useAppSelector(selectLastSelectedDate);

  const checkAndUpdate = () => {
    const today = getTodayDateString();

    const needsUpdate = shouldSelectNewQuote(
      today,
      lastSelectedDate,
      dailyQuoteId,
      allQuotes
    );

    if (needsUpdate) {
      const newQuote = selectQuoteByDate(allQuotes, today);
      if (newQuote) {
        dispatch(
          setDailyQuote({
            quoteId: newQuote.id,
            date: today,
          })
        );
        return true; // Updated
      }
    }

    return false; // No update needed
  };

  return {
    checkAndUpdate,
    isUpToDate: !shouldSelectNewQuote(
      getTodayDateString(),
      lastSelectedDate,
      dailyQuoteId,
      allQuotes
    ),
  };
}
